/**
 * MCSurveyForm Component
 * Public survey form for Meaningful Conversations recipients
 * Mirrors the Leading at Gallo SurveyForm flow
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSurveyByToken, submitSurveyResponse } from '../../services/mcAPI';
import './MCSurveyForm.css';

const MCSurveyForm = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [responses, setResponses] = useState({ q1: null, q2: '' });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    loadSurvey();
  }, [token]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSurveyByToken(token);
      setSurvey(data.survey);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChoiceChange = (value) => {
    setResponses(prev => ({ ...prev, q1: value }));
    // Auto-scroll to next question
    setTimeout(() => {
      const q2El = document.getElementById('mc-question-q2');
      if (q2El) {
        q2El.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const isFormComplete = () => responses.q1 !== null;

  const getProgress = () => {
    let answered = 0;
    if (responses.q1 !== null) answered++;
    if (responses.q2.trim().length > 0) answered++;
    return { answered, total: 2 };
  };

  const handleSubmitClick = () => {
    if (isFormComplete()) {
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      await submitSurveyResponse(token, {
        has_meaningful_conversation: responses.q1 === 'Yes',
        feedback_text: responses.q2.trim() || null
      });

      setSubmitted(true);
      setShowConfirmModal(false);
    } catch (err) {
      setError(err.message);
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- SUBMITTED STATE ----
  if (submitted) {
    return (
      <div className="mc-survey-success-container">
        <div className="mc-survey-success-content">
          <div className="mc-success-icon">
            <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
              <circle cx="40" cy="40" r="40" fill="#10b981"/>
              <path d="M25 40L35 50L55 30" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h1 className="mc-success-title">Thank You!</h1>
          <p className="mc-success-message">Your response has been successfully submitted.</p>
          <p className="mc-success-description">
            Your feedback is valuable and helps ensure leaders are having meaningful
            conversations with their teams.
          </p>
          <div className="mc-success-actions">
            <button onClick={() => navigate('/')} className="mc-survey-btn mc-survey-btn-primary">
              Return to My Surveys
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- LOADING STATE ----
  if (loading) {
    return (
      <div className="mc-survey-container">
        <div className="mc-survey-loading">
          <div className="mc-spinner"></div>
          <p>Loading survey...</p>
        </div>
      </div>
    );
  }

  // ---- ERROR STATE ----
  if (error) {
    return (
      <div className="mc-survey-container">
        <div className="mc-survey-error">
          <div className="mc-error-icon">⚠️</div>
          <h2>Unable to Load Survey</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')} className="mc-survey-btn">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!survey) return null;

  const progress = getProgress();
  const progressPercentage = (progress.answered / progress.total) * 100;

  return (
    <div className="mc-survey-container">
      {/* Header */}
      <div className="mc-survey-header">
        <div className="mc-survey-header-inner">
          <button
            onClick={() => navigate('/')}
            className="mc-survey-btn mc-back-button"
          >
            ← Back to Dashboard
          </button>

          <div className="mc-survey-logo">
            <img src="/gallo-logo.png" alt="Gallo" style={{ height: '150px', marginBottom: '1.5rem' }} />
          </div>

          <h1 className="mc-survey-title">{survey.cycle_name}</h1>
          <p className="mc-survey-subtitle">Meaningful Conversations Survey</p>
          {survey.expires_at && (() => {
            const d = new Date(survey.expires_at);
            const display = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
            return (
              <p className="mc-survey-due-date">
                Due by {display.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                {survey.days_remaining > 0 && ` — ${survey.days_remaining} day${survey.days_remaining !== 1 ? 's' : ''} remaining`}
              </p>
            );
          })()}
        </div>
      </div>

      {/* Questions */}
      <div className="mc-survey-content">
        <div className="mc-survey-inner">
          <div className="mc-survey-questions">
            {/* Question 1: Yes/No */}
            <div id="mc-question-q1" className={`mc-question-card ${responses.q1 !== null ? 'answered' : ''}`}>
              <div className="mc-question-header">
                <span className="mc-question-number">Question 1</span>
                <span className="mc-question-required">Required</span>
              </div>
              <p className="mc-question-text">{survey.questions[0].text}</p>
              <div className="mc-choice-options">
                {survey.questions[0].options.map(option => (
                  <button
                    key={option}
                    type="button"
                    className={`mc-choice-option ${responses.q1 === option ? 'selected' : ''}`}
                    onClick={() => handleChoiceChange(option)}
                  >
                    <span className="mc-choice-indicator">
                      {responses.q1 === option ? '✓' : ''}
                    </span>
                    <span className="mc-choice-label">{option}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Question 2: Text */}
            <div id="mc-question-q2" className={`mc-question-card ${responses.q2.trim() ? 'answered' : ''}`}>
              <div className="mc-question-header">
                <span className="mc-question-number">Question 2</span>
                <span className="mc-question-optional">Optional</span>
              </div>
              <p className="mc-question-text">{survey.questions[1].text}</p>
              <textarea
                className="mc-feedback-textarea"
                value={responses.q2}
                onChange={e => setResponses(prev => ({ ...prev, q2: e.target.value }))}
                placeholder="Share your thoughts here..."
                rows={5}
                aria-label="Feedback text"
              />
            </div>
          </div>

          {/* Progress */}
          <div className="mc-survey-progress">
            <div className="mc-progress-text">
              Question {Math.min(progress.answered + 1, progress.total)} of {progress.total}
            </div>
            <div className="mc-progress-bar">
              <div className="mc-progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
            </div>
          </div>

          {/* Submit */}
          <div className="mc-survey-actions" id="mc-survey-submit-section">
            <button
              id="mc-survey-submit-button"
              onClick={handleSubmitClick}
              disabled={!isFormComplete() || submitting}
              className={`mc-survey-btn mc-survey-btn-primary mc-submit-btn ${!isFormComplete() ? 'disabled' : ''}`}
            >
              {submitting ? 'Submitting...' : 'Submit Survey'}
            </button>
            {!isFormComplete() && (
              <p className="mc-validation-message">Please answer Question 1 before submitting</p>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="mc-modal-overlay" onClick={() => !submitting && setShowConfirmModal(false)}>
          <div className="mc-confirm-modal" onClick={e => e.stopPropagation()}>
            <h2>Confirm Submission</h2>
            <p>Are you sure you want to submit your responses? You will not be able to change your answers after submission.</p>
            <div className="mc-confirm-actions">
              <button
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
                className="mc-survey-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="mc-survey-btn mc-survey-btn-primary"
              >
                {submitting ? 'Submitting...' : 'Confirm Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCSurveyForm;
